import { useEffect, useState } from "react";

import { useRouter } from "next/router";

import { PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE } from "@/components/ContentPromotions/constants";
import useLazyContentPromotionFormResponsesGet from "@/components/ContentPromotions/hooks/useLazyContentPromotionFormResponsesGet";

import useLazyContentPromotionFormQuestionsGet from "@/components/ContentPromotions/hooks/useLazyContentPromotionFormQuestionsGet";
import useLazyContentPromotionFormsGet from "@/components/ContentPromotions/hooks/useLazyContentPromotionFormsGet";
import canUserAccessContentPromotionForm from "@/components/ContentPromotions/utils/canUserAccessContentPromotionForm";
import { User as UserV3 } from "@/types/models/User/v3/User.interface";
import { UserRoleEnum } from "@/users/getHighestRoleLevel/getHighestRoleLevel.constants";
import { useGetUser } from "@/users/v3";
import { ContentPromotionForm } from "@/types/models/ContentPromotionForm.interface";

import useGetLicenses from "@/licenses/hooks/useGetLicenses";
import useGetUserClassrooms from "@/classrooms/hooks/useGetUserClassrooms";
import getMostSeniorUserRole from "@/users/getMostSeniorUserRole";
import { ContentPromotionVenue } from "@/components/ContentPromotions/types/ContentPromotionVenue.enum";
import { District, School } from "@/types/models";
import { TabSelectorForContentPromotionVenue } from "../PromotedAtYourVenuePage/types";
import PromotedAtYourVenuePage from "../PromotedAtYourVenuePage";

const isValidFormId = (
  formIdToCheck: string,
  forms: ContentPromotionForm[],
) => {
  const checkFormId = (form: ContentPromotionForm) => form.id === formIdToCheck;
  return forms.some(checkFormId);
};

interface ContentPromotionDataContainerProps {
  districtUniqueId?: District["unique_id"] | undefined;
  isVenueDataLoading: boolean;
  selector: TabSelectorForContentPromotionVenue;
  schoolUniqueId?: School["unique_id"] | undefined;
}

const ContentPromotionDataContainer = ({
  districtUniqueId,
  schoolUniqueId,
  isVenueDataLoading,
  selector,
}: ContentPromotionDataContainerProps) => {
  // Pull the form-id from the URL query parameters.
  // We'll use the form-id to fetch the corresponding content-promotion
  // form.
  const { query, replace } = useRouter();
  const formIdUrlParam = Array.isArray(query["form_id"])
    ? query["form_id"][0]
    : query["form_id"];

  const venueData = {
    [TabSelectorForContentPromotionVenue.district]: {
      venue: ContentPromotionVenue.district,
      venueId: districtUniqueId,
      pathname: PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE.district,
    },
    [TabSelectorForContentPromotionVenue.school]: {
      venue: ContentPromotionVenue.school,
      venueId: schoolUniqueId,
      pathname: PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE.school,
    },
  };

  const venueId = venueData[selector].venueId;

  // Fetch the user object.
  const { data: userData } = useGetUser();
  const user = userData as UserV3;

  // Identify the user's roles to determine which CP forms
  // they can access.
  const { roles: userRoles } = user || { roles: [] };
  const isAdminUser =
    userRoles.includes(UserRoleEnum.district_admin) ||
    userRoles.includes(UserRoleEnum.school_admin);

  const [selectedForm, setSelectedForm] = useState<ContentPromotionForm>();
  const [relevantForms, setRelevantForms] = useState<ContentPromotionForm[]>();
  const [isLoadingRelevantForms, setIsLoadingRelevantForms] = useState(true);

  // If the user is a teacher, fetch their licenses so that we can
  // determine which content-promotion forms they ought to see.
  const { data: userLicenses, isLoading: isLoadingLicenses } = useGetLicenses({
    userUniqueId: user?.unique_id,
    enabled: !isAdminUser && Boolean(user?.unique_id),
  });

  // If the user is a teacher, fetch their classrooms so that we can
  // determine which content-promotion forms they ought to see.
  const { data: userClassroomsData, isLoading: isLoadingClassrooms } =
    useGetUserClassrooms({
      userUniqueId: user?.unique_id,
      enabled: !isAdminUser && Boolean(user?.unique_id),
    });

  // Initialize the fetch for content-promotion forms.
  const [
    fetchContentPromotionForms,
    { data: allForms, loading: isLoadingForms },
  ] = useLazyContentPromotionFormsGet({
    districtUniqueId: districtUniqueId || null,
    schoolUniqueId: schoolUniqueId || null,
  });

  // Initialize the fetch for form questions.
  const [fetchFormQuestions, { data: formQuestions }] =
    useLazyContentPromotionFormQuestionsGet(selectedForm?.product_slug);

  // Initialize the fetch for form responses.
  const [
    fetchFormResponses,
    { data: formResponses, loading: isLoadingFormResponses },
  ] = useLazyContentPromotionFormResponsesGet(selectedForm?.id ?? "");

  // Fetch content-promotion forms.
  useEffect(() => {
    if (!isVenueDataLoading && venueId) {
      fetchContentPromotionForms();
    }
  }, [isVenueDataLoading, venueId]);

  // When the selected-form is identified, fetch the form's
  // responses and questions.
  useEffect(() => {
    if (selectedForm?.id) {
      fetchFormResponses();
      fetchFormQuestions();
    }
  }, [selectedForm]);

  // When the forms are fetched, identify the relevant forms, that is, the
  // forms that user is authorized to access. They will be displayed as
  // chips in the UI.
  useEffect(() => {
    // if (!allForms) return;

    if (!allForms || isLoadingForms) return;

    if (!isLoadingForms && allForms) {
      if (isAdminUser) {
        // Admins can see all of the forms that exist in their venue.
        setRelevantForms(allForms);
        setIsLoadingRelevantForms(false);
      } else {
        // non-admin users (teachers)
        if (isLoadingLicenses || isLoadingClassrooms) return;

        // Whereas, non-admins can only see published forms that were configured for
        // their grade-levels and/or subject-product areas.
        if (
          !isLoadingLicenses &&
          Boolean(userLicenses) &&
          !isLoadingClassrooms &&
          Boolean(userClassroomsData)
        ) {
          const relevantForms = allForms?.filter((form) => {
            return (
              form.is_published &&
              canUserAccessContentPromotionForm({
                user,
                licenses: userLicenses,
                classrooms: userClassroomsData?.results,
                form,
              })
            );
          });

          setRelevantForms(relevantForms);
          setIsLoadingRelevantForms(false);
        }
      }
    }
  }, [
    allForms,
    isAdminUser,
    isLoadingForms,
    isLoadingLicenses,
    userLicenses,
    isLoadingClassrooms,
    userClassroomsData,
  ]);

  // When the relevant forms have been identified, identify the specific form
  // that ought to be displayed. It will appear as the selected form in the
  // UI (the selected chip). If there is a valid form-id in the url, use that.
  // If not, the selected form will be the first form returned from the
  // fetch for forms.
  useEffect(() => {
    if (relevantForms && relevantForms.length > 0) {
      if (formIdUrlParam && isValidFormId(formIdUrlParam, relevantForms)) {
        setSelectedForm(
          relevantForms.find((form) => form.id === formIdUrlParam),
        );
      } else {
        // There isn't a form-id in the url. Pick the first one
        // in the list of relevant forms.
        const firstForm = relevantForms?.[0];
        void replace(
          `${venueData[selector].pathname}?form_id=${firstForm?.id}`,
          undefined,
          {
            shallow: true,
          },
        );
        setSelectedForm(firstForm);
      }
    } else {
      // Else, there aren't any relevant-forms. Update the selected-form state.
      setSelectedForm(undefined);
    }
  }, [relevantForms, formIdUrlParam]);

  const mostSeniorUserRole = getMostSeniorUserRole(userRoles);

  const isBannerVisibleToPublishForm =
    (mostSeniorUserRole === UserRoleEnum.district_admin &&
      Boolean(districtUniqueId)) ||
    (mostSeniorUserRole === UserRoleEnum.school_admin &&
      Boolean(schoolUniqueId));

  return (
    <PromotedAtYourVenuePage
      mostSeniorUserRole={mostSeniorUserRole}
      venue={venueData[selector].venue}
      urlPathname={venueData[selector].pathname}
      fetchContentPromotionForms={fetchContentPromotionForms}
      selector={selector}
      selectedForm={selectedForm}
      selectedFormResponses={formResponses}
      selectedFormQuestions={formQuestions}
      relevantForms={relevantForms}
      isBannerVisibleToPublishForm={isBannerVisibleToPublishForm}
      isLoadingFormResponses={isLoadingFormResponses}
      isLoadingRelevantForms={isLoadingRelevantForms}
      setIsLoadingRelevantForms={setIsLoadingRelevantForms}
    />
  );
};

export default ContentPromotionDataContainer;
